import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { util as sdkUtil, createImageVariantConfig } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import { clearGuestCart, getCartFromCurrentUser, getGuestCart, mergeCarts } from '../util/cart';
import {
  getStatesNeedingProviderAttention,
  getStatesNeedingCustomerAttention,
  getSupportedProcessesInfo,
} from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';

// Custom variants only — avoid Sharetribe square-* center-crops that cut heads.
// fit:scale keeps the full portrait; CSS top-frames the square in the avatar UI.
export const TOPBAR_AVATAR_VARIANTS = ['topbar-avatar', 'topbar-avatar-2x'];
export const PROFILE_SETTINGS_AVATAR_VARIANTS = [
  'profile-settings-avatar',
  'profile-settings-avatar-2x',
];
const PORTRAIT_AVATAR_ASPECT = 4 / 3;
const PORTRAIT_AVATAR_FIT = { fit: 'scale' };

export const CURRENT_USER_PROFILE_IMAGE_QUERY_PARAMS = {
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.square-xsmall',
    'variants.square-xsmall2x',
    ...TOPBAR_AVATAR_VARIANTS.map(variant => `variants.${variant}`),
    ...PROFILE_SETTINGS_AVATAR_VARIANTS.map(variant => `variants.${variant}`),
  ],
  'imageVariant.square-xsmall': sdkUtil.objectQueryString({
    w: 40,
    h: 40,
    fit: 'crop',
  }),
  'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
    w: 80,
    h: 80,
    fit: 'crop',
  }),
  ...createImageVariantConfig('topbar-avatar', 40, PORTRAIT_AVATAR_ASPECT, PORTRAIT_AVATAR_FIT),
  ...createImageVariantConfig('topbar-avatar-2x', 80, PORTRAIT_AVATAR_ASPECT, PORTRAIT_AVATAR_FIT),
  ...createImageVariantConfig(
    'profile-settings-avatar',
    96,
    PORTRAIT_AVATAR_ASPECT,
    PORTRAIT_AVATAR_FIT
  ),
  ...createImageVariantConfig(
    'profile-settings-avatar-2x',
    240,
    PORTRAIT_AVATAR_ASPECT,
    PORTRAIT_AVATAR_FIT
  ),
};

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.resolve({ hasListings: false });
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      return { hasListings: !!hasPublishedListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk()).unwrap();
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk()).unwrap();
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

const fetchCurrentUserNotificationsPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  const statesNeedingProviderAttention = getStatesNeedingProviderAttention() || [];
  const statesNeedingCustomerAttention = getStatesNeedingCustomerAttention() || [];
  // Match InboxPage: only count transactions from processes the client app supports.
  // Excludes stock-management processes like cart-stock-process.
  const processNames = getSupportedProcessesInfo().map(p => p.name);

  const paramsForSales = {
    only: 'sale',
    processNames,
    states: statesNeedingProviderAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };
  const paramsForOrders = {
    only: 'order',
    processNames,
    states: statesNeedingCustomerAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  return Promise.all([
    sdk.transactions.query(paramsForSales),
    sdk.transactions.query(paramsForOrders),
  ])
    .then(([sales, orders]) => {
      const saleNotificationsCount = sales.data.data.length;
      const orderNotificationsCount = orders.data.data.length;
      return { saleNotificationsCount, orderNotificationsCount };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserNotificationsThunk()).unwrap();
};

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    ...CURRENT_USER_PROFILE_IMAGE_QUERY_PARAMS,
  };

  const mergeGuestCartAfterLogin = currentUser => {
    if (!afterLogin) {
      return Promise.resolve(currentUser);
    }

    const guestCart = getGuestCart();
    if (Object.keys(guestCart).length === 0) {
      return Promise.resolve(currentUser);
    }

    const privateData = currentUser?.attributes?.profile?.privateData || {};
    const mergedCart = mergeCarts(getCartFromCurrentUser(currentUser), guestCart);

    return sdk.currentUser
      .updateProfile(
        {
          privateData: {
            ...privateData,
            cart: mergedCart,
          },
        },
        { expand: true }
      )
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        clearGuestCart();
        return mergeCurrentUser(currentUser, entities[0]);
      })
      .catch(e => {
        log.error(e, 'guest-cart-merge-failed');
        return currentUser;
      });
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      return currentUser;
    })
    .then(mergeGuestCartAfterLogin)
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowInProgress: false,
    currentUserShowError: null,
    currentUserHasListings: false,
    currentUserHasListingsError: null,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserNotificationCountError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowInProgress = false;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsError = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;

      state.currentUserNotificationCountError = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowInProgress = true;
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUserShowInProgress = false;
        const mergedUser = mergeCurrentUser(state.currentUser, action.payload);
        const hadVerifiedEmail = state.currentUser?.attributes?.emailVerified === true;
        if (hadVerifiedEmail && mergedUser?.attributes?.emailVerified === false) {
          mergedUser.attributes = { ...mergedUser.attributes, emailVerified: true };
        }
        state.currentUser = mergedUser;
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserShowInProgress = false;
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListings = action.payload.hasListings;
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserHasOrdersError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const { clearCurrentUser, setCurrentUser, setCurrentUserHasOrders } = userSlice.actions;

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};
